import { Formik, FormikProps, useFormikContext } from 'formik';
import React, { MutableRefObject, useMemo } from 'react';
import * as Yup from 'yup';

import { RouteContext } from 'src/context';
import RowDivider from 'src/legacy/components/RowDivider';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { GoogleAuthButton } from 'src/legacy/components/UI/Buttons/GoogleAuthButton';
import {
  OnboardingStep,
  TrackedEventsType,
} from 'src/routes/onboarding/setup/types';
import { GraySmall } from 'src/theme/colors';
import { UrlUtils } from 'src/utils';
import { TrackEvent } from 'src/utils/AnalyticsUtils';
import { GetPasswordValidation, SignInWithGoogle } from 'src/utils/AuthUtils';
import {
  removeSpaceFromString,
  removeSpaceToLowerCase,
} from 'src/utils/StringUtils';

import OnboardingButton from '../OnboardingButton';

import { OnboardingLoginFormFooterActions } from './OboardingLoginFormFooterActions';
import {
  OnboardingLoginFormProps,
  OnboardingLoginFormValues,
} from './onboardingFormTypes';
import { OnboardingLoginFormFields } from './OnboardingLoginFormFields';

export const parseOnboardingLoginSubmitValues = <
  T extends OnboardingLoginFormValues,
>(
  values: T,
  isPortalLogin: boolean = false,
) => {
  const parsedValues = {
    ...values,
    email:
      // for backward compatibility do not lowercase email if login
      // only in onboarding to create new portal lowercase email
      !isPortalLogin
        ? removeSpaceToLowerCase(values.email)
        : removeSpaceFromString(values.email), // update email to remove space and lowercase
  };
  return parsedValues;
};

export const trackOnboardingLoginEvent = <T extends OnboardingLoginFormValues>(
  values: T,
  trackedEvents: TrackedEventsType,
  onAnalyticsEventTracked: (eventName: OnboardingStep) => void,
) => {
  if (!trackedEvents['create-account']) {
    TrackEvent('Onboarding - Portal Account Created', {
      email: values.email,
    });
    onAnalyticsEventTracked('create-account');
  }
};

export const useOnboardingLoginFormValidation = ({
  isPortalLogin = false,
  isAdminLogin = false,
}: {
  isPortalLogin?: boolean;
  isAdminLogin?: boolean;
}) => {
  const validationSchema = useMemo(
    () =>
      Yup.object()
        .strict(true)
        .shape({
          email: Yup.string()
            .email('Invalid email')
            .required('Email is required'),
          password: GetPasswordValidation({
            isPortalLogin,
            isAdminLogin,
          }),
        }),
    [isPortalLogin, isAdminLogin],
  );

  return validationSchema;
};

export interface OnboardingLoginProps {
  showOnboardingLoginActions?: boolean;
}

export const OnboardingLogin = ({
  isPortalLogin = false,
  isAdminLogin = false,
  showOnboardingLoginActions = false,
}: {
  isPortalLogin?: boolean;
  isAdminLogin?: boolean;
} & OnboardingLoginProps) => {
  const { query } = React.useContext(RouteContext);

  const { isSubmitting } = useFormikContext<OnboardingLoginFormValues>();

  // customState is the state that we set in the oauth sign in flow
  // In our implementation, this is used to save the redirects that we want
  // to do after the flow
  const [customState, setCustomState] = React.useState('');

  // This useEffect sets the customState to track the what the oauthCompleteURL will be.
  // This value is passed as the customState to federatedSignIn.
  // In the signup flow, we redirect to the second step of the flow.
  React.useEffect(() => {
    if (isPortalLogin) {
      return;
    }

    // when this is a signup, we want to redirect to the signup page again
    // since, the user is logged in now, the next step will be called
    // Also retain the template_id if it is present.
    let cs = `signup?step=company-setup&authSource=Google`;
    if (query.template_id) {
      cs += `&${UrlUtils.FormatUrlQueryToString({
        template_id: query.template_id,
        template_name: query.template_name,
      })}`;
    }
    setCustomState(cs);
  }, [query, isPortalLogin]);

  return (
    <>
      <GoogleAuthButton
        onClick={() => {
          void SignInWithGoogle(customState);
        }}
        noBorderRadius
      />

      <RowDivider mt={3.25} mb={0.75}>
        <BaseTypography style={{ color: GraySmall }} fontType="12Medium">
          OR
        </BaseTypography>
      </RowDivider>

      <OnboardingLoginFormFields
        isAdminLogin={isAdminLogin}
        isPortalLogin={isPortalLogin}
      />

      <OnboardingButton
        type="submit"
        isLoading={isSubmitting}
        data-testid="submit-button"
        color="primary"
        variant="contained"
        fullWidth
        htmlId="create-account-submit-button"
      >
        {isPortalLogin || isAdminLogin ? 'Sign in' : 'Create an account'}
      </OnboardingButton>

      <OnboardingLoginFormFooterActions
        showOnboardingLoginActions={showOnboardingLoginActions}
      />
    </>
  );
};

/**
 * Onboarding login challenge form. This form is used to start the login process for
 * an internal user. This is used in the following scenarios:
 * 1. portal login flow: enter email and password
 * 2. admin login flow: enter email and go through the custom login flow with pass code
 * 3. create portal (onboarding) flow: enter email and password which will either
 * be a new user or existing user. Existing user will have their info prefilled in the
 * create portal flow.
 */
export const OnboardingLoginForm = React.forwardRef(
  (
    {
      initialValues,
      handleSubmitDone,
      showOnboardingLoginActions,
      trackedEvents,
      onAnalyticsEventTracked,
    }: OnboardingLoginFormProps & OnboardingLoginProps,
    ref,
  ) => {
    const { pathname } = React.useContext(RouteContext);
    const isAdminLogin = pathname === '/admin'; // admin login flow
    const isPortalLogin = pathname === '/login' || pathname === '/add-account'; // portal login flow

    const validationSchema = useOnboardingLoginFormValidation({
      isAdminLogin,
      isPortalLogin,
    });

    const onSubmit = async (values: OnboardingLoginFormValues) => {
      const parsedValues = parseOnboardingLoginSubmitValues(
        values,
        isPortalLogin,
      );

      const isSuccess = await handleSubmitDone(parsedValues);

      if (isSuccess) {
        trackOnboardingLoginEvent(
          parsedValues,
          trackedEvents,
          onAnalyticsEventTracked,
        );
      }
    };

    return (
      <>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          innerRef={
            ref as MutableRefObject<FormikProps<OnboardingLoginFormValues>>
          }
        >
          {({ handleSubmit }) => (
            <form noValidate onSubmit={handleSubmit} data-testid="testingform">
              <OnboardingLogin
                isPortalLogin={isPortalLogin}
                isAdminLogin={isAdminLogin}
                showOnboardingLoginActions={showOnboardingLoginActions}
              />
            </form>
          )}
        </Formik>
      </>
    );
  },
);
